import { StandardObject } from "utils";
import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
  GenericResponse,
  CommonResponse,
  Sortable,
} from "./utils";

export interface TaskService {
  listTask: ReturnType<typeof listTask>;
  getTaskDetail: ReturnType<typeof getTaskDetail>;
  putTaskDetail: ReturnType<typeof putTaskDetail>;
  listJob: ReturnType<typeof listJob>;
  getJobDetail: ReturnType<typeof getJobDetail>;
  putJobDetail: ReturnType<typeof putJobDetail>;
  deleteJobDetail: ReturnType<typeof deleteJobDetail>;
  postJob: ReturnType<typeof postJob>;
}

interface TaskSearchCriteria {
  category?: string;
}

export interface TaskDTO {
  name: string;
  category: string;
  isJobAssociated: boolean;
  lastUpdateTime: string;
  lastUpdateAdmin: string;
}

export type TaskInputType =
  | "OBJECT"
  | "ENUM"
  | "UUID"
  | "STRING"
  | "INTEGER"
  | "NUMERIC"
  | "ALPHANUMERIC"
  | "DATE"
  | "DATETIME"
  // | "ARRAY" // Not Implemented yet
  | "BOOLEAN";
export interface TaskInputParameter {
  name: string;
  label: string;
  type: TaskInputType;
  elementType? : TaskInputType;
  options: string[];
  description: string;
  mandatory: boolean;
  pattern: string;
  patternDescription: string;
}
export interface TaskDetail extends TaskDTO {
  inputSchema: TaskInputParameter[];
  defaultInput: StandardObject;
  defaultCronExpression: string;
}

export interface UpdateTaskDetailRequest {
  defaultInput: StandardObject;
  defaultCronExpression: string;
}

export type JobType = "ONE_OFF" | "CRONJOB";
export type UpdatedJobStatus = "SCHEDULED" | "CANCELLED" | "PAUSED" | "REPLACED";
export type JobStatus = UpdatedJobStatus | "COMPLETED"  | "FAILED";
interface TaskJobSearchCriteria extends Sortable {
  taskName: string;
  jobIds?: number;
  jobName?: string;
  jobType?: JobType[];
  jobStatus?: JobStatus[];
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
  lastUpdateAdmin?: string;
}

export interface JobDetailDTO {
  id: number;
  taskName?: string;
  taskCategory?: string;
  jobName?: string;
  jobType?: JobType;
  jobStatus: JobStatus;
  lastUpdateTime?: string;
  lastUpdateAdmin?: string;
  startTime?: string;
  endTime?: string;
  cronExpression?: string;
  jobInput: StandardObject;
}

export interface UpdateJobRequest {
  jobInput: StandardObject;
  cronExpression: string;
  jobStatus: JobStatus;
}

export interface ScheduleJobRequest {
  jobName: string;
  jobType: JobType;
  jobInput?: StandardObject;
  cronExpression?: string;
  startTime?: string;
  endTime?: string;
  replaceExisting?: boolean;
}

// Tasks
export const listTask: WrappedCashifyPageableQuery<
  TaskSearchCriteria,
  TaskDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(`${urlPrefix}/api/v1/task`, queryParams);
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getTaskDetail: WrappedCashifyFunction<
  { taskName: string },
  GenericResponse<TaskDetail>
> = ({ urlPrefix, options }) => {
  return async ({ taskName }) => {
    let url = `${urlPrefix}/api/v1/task/${taskName}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putTaskDetail: WrappedCashifyFunction<
  { taskName: string; detail: UpdateTaskDetailRequest },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ taskName, detail }) => {
    let url = `${urlPrefix}/api/v1/task/${taskName}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

// Jobs
export const listJob: WrappedCashifyPageableQuery<
  TaskJobSearchCriteria,
  JobDetailDTO
> = ({ urlPrefix, options }) => {
  return async (params) => {
    const { taskName, ...queryParams } = params;
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/task/${taskName}/job`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getJobDetail: WrappedCashifyFunction<
  { taskName: string; jobId: number },
  GenericResponse<JobDetailDTO>
> = ({ urlPrefix, options }) => {
  return async ({ taskName, jobId }) => {
    let url = `${urlPrefix}/api/v1/task/${taskName}/job/${jobId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putJobDetail: WrappedCashifyFunction<
  { taskName: string; jobId: number; detail: UpdateJobRequest },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ taskName, jobId, detail }) => {
    let url = `${urlPrefix}/api/v1/task/${taskName}/job/${jobId}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteJobDetail: WrappedCashifyFunction<
  { taskName: string; jobId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ taskName, jobId }) => {
    let url = `${urlPrefix}/api/v1/task/${taskName}/job/${jobId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const postJob: WrappedCashifyFunction<
  { taskName: string; fields: ScheduleJobRequest },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ taskName, fields }) => {
    let url = `${urlPrefix}/api/v1/task/${taskName}/job`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(fields),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
